<template>
  <div>
    <v-select
      v-bind="$attrs"
      :model-value="localValue"
      :options="localOptions"
      :reduce="option => option.value"
      :clearable="false"
      :placeholder="placeholder"
      :calculate-position="withPopper"
      append-to-body
      class="dropdown"
      @update:model-value="handleUpdate"
    >
      <template #open-indicator="{ attributes }">
        <img
          v-bind="attributes"
          src="@/assets/expand-more.svg"
          class="icon"
        >
      </template>
    </v-select>
  </div>
</template>

<script>
import { defineComponent, ref, watch } from 'vue';
import VSelect from 'vue-select';
import { createPopper } from '@popperjs/core';

export default defineComponent({
  name: 'DropdownInputComponent',

  emits: ['select'],

  components: {
    VSelect,
  },

  props: {
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
  },

  setup(props, { emit }) {
    const localValue = ref(null);
    const localOptions = ref(props.options.map((opt, index) => ({ ...opt, value: index.toString() })));

    function withPopper(dropdownList, component, { width }) {
      /**
       * We need to explicitly define the dropdown width since
       * it is usually inherited from the parent with CSS.
       */
      dropdownList.style.width = width;

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      });

      return () => popper.destroy();
    }

    function handleUpdate(index) {
      localValue.value = index;
      emit('select', index);
    }

    watch(() => props.value, (newVal) => {
      if (newVal !== localValue.value) {
        localValue.value = newVal;
      }
    });

    return {
      localValue,
      localOptions,
      withPopper,
      handleUpdate,
    };
  },
});
</script>

<style lang="scss" scoped>
  @import 'vue-select/dist/vue-select.css';

  .dropdown {
    height: 48px;
    max-width: 100%;

    @media screen and (min-width: $desktop_s_breakpoint - 1px) {
      height: 68px;
    }

    &:deep {
      &.vs--open {
        .vs__dropdown-toggle {
          outline: 1px solid #1E58F2;
          border-color: #1E58F2;
        }

        .vs__selected {
          display: none;
        }
      }

      .vs__dropdown-toggle {
        height: 100%;
        cursor: pointer;
        border-radius: 4px;
        border-color: #E1E6EC;
      }

      .vs__actions {
        padding-right: 16px;
      }

      .vs__selected-options {
        padding-left: 10px;
        flex-wrap: nowrap;
        min-width: 0;
        flex: 1;
      }

      .vs__selected {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 38px;
        font-size: 16px;

        @media screen and (min-width: $desktop_s_breakpoint - 1px) {
          line-height: 58px;
          font-size: 28px;
        }
      }

      .vs__search {
        font-size: 16px;
        font-family: Avenir, Helvetica, Arial, sans-serif;

        @media screen and (min-width: $desktop_s_breakpoint - 1px) {
          font-size: 28px;
        }

        &::placeholder {
          color: #C6C7C9;
        }
      }
    }
  }

  :global(.vs__dropdown-menu) {
    max-height: 315px;
    border-radius: 8px;
    border: 1px solid #E1E6EC;
    padding-left: 4px;
    padding-right: 4px;
  }

  :global(.vs__dropdown-menu) {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 32px;

    @media screen and (min-width: $desktop_s_breakpoint - 1px) {
      font-size: 18px;
    }
  }

  :global(.vs__dropdown-option) {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 32px;
    font-weight: 400;
    padding-top: 0;
    padding-bottom: 0;
    height: 32px; // for empty labels
    letter-spacing: 0.25px;
    color: #2A3746;
    border-radius: 4px;

    @media screen and (min-width: $desktop_s_breakpoint - 1px) {
      font-size: 18px;
    }
  }

  :global(.vs__dropdown-option--highlight, .vs__dropdown-option--selected) {
    background-color: #EDF2FE;
    color: #2A3746;
  }
</style>
